/**
 * Where a seated continuation piece's slab is cut (user, 2026-11-04).
 *
 * A seated arm's top is one rectangle square to the arm — its cabinets, the
 * 1" back panel and the knee overhang (`seatedPeninsulaTop.js`). That fits an
 * arm meeting its run square. A continuation piece meets the run it carries on
 * from, and the next piece, at an angle, and the rectangle's outer corner then
 * stands past the line of that run: 14.8" past the plane of the wing wall's
 * face on Robinson/Mckinzie / Scan 1's 45° piece. So the slab is cut along the
 * line of each run the piece meets at an angle — the wall it continues from,
 * the piece before it, the piece after it — keeping the side its own cabinets
 * stand on. At a square or straight joint the cut removes nothing.
 *
 * A cut is held in the piece's OWN frame (`a` along its line from its start,
 * `n` toward its cabinets) — a point on the line and its direction — so it
 * moves with the piece. It is stamped on the arm once, where the generator
 * builds a layout's arms (`withArmCuts`), and saved with the arm in the
 * cabinet layout (`arm.cuts`), so the run engine's re-solve cuts the same way.
 * Everything at the arm's back reads it: the seated slab, the stools' span and
 * the 1" back panel of an unseated piece (user, 2026-11-04: "yes" to cutting
 * the panel too — square, it stood 0.71" past the line). Only continuation
 * pieces carry cuts; a drawn run's bends are the run engine's own
 * (`bentRunFill.js`).
 *
 * Pure. Metres.
 */
const { getWallAxes } = require('./wallMath');
const { clipToHalfPlane } = require('../geometry/halfPlaneClip');
const { objectFootprint } = require('../geometry/objectFootprint');
const { pointSegmentDistance } = require('../geometry/segmentGeometry');

/** A joint this close to straight (|sin| of the turn) is no joint. */
const STRAIGHT_SIN = 0.02;
/** How far into the piece, and toward its cabinets, the kept side is read. */
const PROBE = 0.3;

/** The runs a continuation piece meets: its wall or the piece before, and the piece after. */
function neighboursOf(arm, walls) {
  const c = arm?.peninsulaSource?.continuation;
  if (!c) return [];
  const piece = (index) =>
    (walls || []).find((w) => {
      const o = w?.peninsulaSource?.continuation;
      return w !== arm && o && o.wallId === c.wallId && o.index === index;
    });
  const before =
    c.index === 0
      ? (walls || []).find((w) => w?.id === c.wallId)
      : piece(c.index - 1);
  const after = c.index + 1 < (c.count ?? 0) ? piece(c.index + 1) : null;
  return [before, after].filter(Boolean);
}

/**
 * The cuts on a seated piece's slab, in its own frame: `{ a, da, dn }`, the
 * line through `(a, 0)` running `(da, dn)`.
 *
 * @param {Object} arm  the piece's virtual wall
 * @param {Object[]} walls  the layout's walls, real and virtual
 * @returns {Array<{a:number, da:number, dn:number}>}
 */
function slabCuts(arm, walls) {
  const ax = getWallAxes(arm);
  if (!ax) return [];
  const out = [];
  for (const wall of neighboursOf(arm, walls)) {
    const wx = getWallAxes(wall);
    if (!wx) continue;
    const da = wx.wallDir.x * ax.wallDir.x + wx.wallDir.z * ax.wallDir.z;
    const dn = wx.wallDir.x * ax.wallNormal.x + wx.wallDir.z * ax.wallNormal.z;
    if (Math.abs(dn) < STRAIGHT_SIN) continue; // in line: nothing to cut
    // The joint is the piece's end that lies on the run it meets — either
    // end, since a piece's wall may run toward the joint or away from it.
    const atStart = pointSegmentDistance(ax.start, wx.start, wx.end);
    const atEnd = pointSegmentDistance(ax.end, wx.start, wx.end);
    out.push({ a: atStart <= atEnd ? 0 : ax.wallLen, da, dn });
  }
  return out;
}

/** The cut as a half-plane test on world points: kept where it is <= 0. */
function keepTest(arm, cut) {
  const ax = getWallAxes(arm);
  const local = (p) => ({
    a: (p.x - ax.start.x) * ax.wallDir.x + (p.z - ax.start.z) * ax.wallDir.z,
    n:
      (p.x - ax.start.x) * ax.wallNormal.x +
      (p.z - ax.start.z) * ax.wallNormal.z,
  });
  // Which side of the line a point is on, signed so the piece's own cabinets
  // — a little in from the cut end, toward the cabinet side — are kept.
  const side = (a, n) => (a - cut.a) * cut.dn - n * cut.da;
  const inward = cut.a > 0 ? -PROBE : PROBE;
  const sign = Math.sign(side(cut.a + inward, PROBE)) || 1;
  return (p) => {
    const q = local(p);
    return -sign * side(q.a, q.n);
  };
}

/**
 * A slab's outline cut by `cuts`, in world points — unchanged when there are
 * none or none reaches it.
 */
function cutOutline(outline, arm, cuts) {
  let out = outline;
  for (const cut of cuts || []) {
    if (out.length < 3) break;
    out = clipToHalfPlane(out, keepTest(arm, cut));
  }
  return out;
}

/**
 * The part of `span` (the piece's own params) where everything out to `reach`
 * behind the piece's line stays on its side of every cut — for the stools, the
 * back panel, the knee overhang and the stool standing under it. Null when
 * nothing is left.
 */
function seatableSpan(span, arm, cuts, reach) {
  const ax = getWallAxes(arm);
  if (!ax || !span) return span;
  const outerN = -reach;
  let { start, end } = span;
  for (const cut of cuts || []) {
    // The outer edge (n = outerN) and the back line (n = 0) both stay kept.
    for (const n of [0, outerN]) {
      if (Math.abs(cut.dn) < 1e-12) continue;
      // Where the cut line crosses this edge.
      const a = cut.a + (n * cut.da) / cut.dn;
      if (cut.a === 0) start = Math.max(start, a);
      else end = Math.min(end, a);
    }
  }
  return end - start > 1e-6 ? { start, end } : null;
}

const area = (P) =>
  Math.abs(
    P.reduce((sum, p, i) => {
      const q = P[(i + 1) % P.length];
      return sum + p.x * q.z - q.x * p.z;
    }, 0) / 2
  );

/**
 * A seated top with its plan outline cut (`polygonCorners`, as an angled
 * end's counter carries it); unchanged when no cut reaches it.
 */
function withSlabCuts(top, arm, cuts) {
  if (!cuts?.length || !top?.dimensions) return top;
  const fp = objectFootprint(top);
  if (!fp) return top;
  const cut = cutOutline(fp, arm, cuts);
  if (cut.length < 3 || area(cut) >= area(fp) - 1e-9) return top;
  return { ...top, polygonCorners: cut.map(({ x, z }) => ({ x, z })) };
}

/**
 * Each arm with its cuts stamped on it as `cuts`, an arm with none returned as
 * it was. `walls` are the room's walls and the layout's arms, so a piece finds
 * the wall it continues from and the pieces either side of it. Computed here
 * because only here does a piece still know its continuation; the run
 * engine's rebuilt arm reads the saved `cuts` instead.
 */
function withArmCuts(arms, walls) {
  return (arms || []).map((arm) => {
    const cuts = slabCuts(arm, walls);
    return cuts.length ? { ...arm, cuts } : arm;
  });
}

module.exports = {
  slabCuts,
  cutOutline,
  seatableSpan,
  withSlabCuts,
  withArmCuts,
};
